import type { Command } from '../types';

const STORAGE_KEYS = {
  RECENT_COMMANDS: 'commandPalette:recentCommands',
  COMMAND_USAGE: 'commandPalette:commandUsage',
} as const;

export interface CommandUsage {
  commandId: string;
  count: number;
  lastUsed: number;
}

export class CommandPaletteStorage {
  private static readonly MAX_RECENT_COMMANDS = 10;

  static getRecentCommands(): string[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.RECENT_COMMANDS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  static addRecentCommand(commandId: string): void {
    try {
      const recents = this.getRecentCommands();

      // Remove if already exists
      const filtered = recents.filter(id => id !== commandId);

      // Add to front
      const updated = [commandId, ...filtered].slice(0, this.MAX_RECENT_COMMANDS);

      localStorage.setItem(STORAGE_KEYS.RECENT_COMMANDS, JSON.stringify(updated));
    } catch (error) {
      console.warn('Failed to save recent command:', error);
    }
  }

  static getCommandUsage(): Record<string, CommandUsage> {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.COMMAND_USAGE);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  static incrementCommandUsage(commandId: string): void {
    try {
      const usage = this.getCommandUsage();
      const current = usage[commandId] || { commandId, count: 0, lastUsed: 0 };

      usage[commandId] = {
        ...current,
        count: current.count + 1,
        lastUsed: Date.now(),
      };

      localStorage.setItem(STORAGE_KEYS.COMMAND_USAGE, JSON.stringify(usage));
    } catch (error) {
      console.warn('Failed to save command usage:', error);
    }
  }

  static clearStorage(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.RECENT_COMMANDS);
      localStorage.removeItem(STORAGE_KEYS.COMMAND_USAGE);
    } catch (error) {
      console.warn('Failed to clear command palette storage:', error);
    }
  }
}